import React from 'react';
import { EyeIcon, PencilIcon, PlusIcon, QuoteIcon, SparklesIcon, SunIcon } from 'lucide-react';
import { useHRStore } from '../../hooks/useHRStore';
import type { Navigate } from '../../types/hr';
import { displayName } from '../../utils/employees';
import { formatDate } from '../../utils/format';
import { describePolicy } from '../../utils/policy';
import { Avatar } from '../ui/Avatar';
import { Badge } from '../ui/Badge';
import { Button } from '../ui/Button';
import { TbdBadge } from '../ui/TbdBadge';
export function PoliciesList({
  onNavigate
}: {
  onNavigate: Navigate;
}) {
  const {
    policies,
    employees
  } = useHRStore();
  return <div data-id="element-727" data-mp="14t4jx50zm6o2">
      <div className="mb-5 flex flex-wrap items-center justify-between gap-3" data-id="element-728" data-mp="14winggle0lx3">
        <div className="flex items-center gap-3" data-id="element-729" data-mp="18xkkqrvqunwf">
          <h2 className="text-lg font-semibold text-navy" data-id="element-730" data-mp="2ga49hsvya7ee">Policies</h2>
          <span className="inline-flex items-center gap-1.5 rounded-full bg-emerald-50 px-3 py-1 text-xs font-semibold text-emerald-700 ring-1 ring-inset ring-emerald-600/15" data-id="element-731" data-mp="2gbxhbbiauxnz">
            <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" aria-hidden="true" data-id="element-732" data-mp="2ztlryqnkep62" />
            {policies.length} active {policies.length === 1 ? 'policy' : 'policies'}
          </span>
        </div>
        <Button variant="primary" icon={PlusIcon} onClick={() => onNavigate({
        page: 'policy-edit'
      })} data-id="element-733" data-mp="2pue59cuxxi6a">
          New policy
        </Button>
      </div>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2" data-id="element-734" data-mp="2gvso9m54ab7m">
        {policies.map(p => {
        const assigned = employees.filter(e => e.policyId === p.id);
        const Icon = p.id === 'newhire' ? SparklesIcon : SunIcon;
        const {
          sentence
        } = describePolicy(p.form);
        return <article key={p.id} className="flex flex-col rounded-2xl bg-white p-6 shadow-soft ring-1 ring-slate-900/[0.04]" data-id="element-735" data-mp="227o1o72y1fjk">
              <div className="flex items-start justify-between gap-4" data-id="element-736" data-mp="1y488xltzvzc5">
                <span className={`flex h-12 w-12 items-center justify-center rounded-2xl ${p.id === 'newhire' ? 'bg-flame/10 text-flame' : 'bg-azure/10 text-azure'}`} data-id="element-737" data-mp="1t6zpzj10vr9u">
                  <Icon className="h-6 w-6" data-id="element-738" data-mp="1njo3uky0mmtg" />
                </span>
                <div className="flex gap-2" data-id="element-739" data-mp="1nhtemufpo8jk">
                  {p.hasTbd && <TbdBadge data-id="element-740" data-mp="19l6k0yb8dugt" />}
                  <Badge tone="green" dot data-id="element-741" data-mp="19lzf320tuvj4">
                    Active
                  </Badge>
                </div>
              </div>
              <h3 className="mt-5 text-lg font-semibold text-navy" data-id="element-742" data-mp="2292dlu5i769d">{p.name}</h3>
              <p className="mt-1 text-sm text-muted" data-id="element-743" data-mp="22b6es4c0pr5y">{p.tagline}</p>

              <div className="mt-5 rounded-xl bg-canvas p-4" data-id="element-744" data-mp="277dn23rvzer2">
                <p className="flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wider text-muted" data-id="element-745" data-mp="1sjiucndvpadz">
                  <QuoteIcon className="h-3 w-3" aria-hidden="true" data-id="element-746" data-mp="1xh28zoehnp6g" /> Rule summary · read-only
                </p>
                <p className="mt-1.5 text-sm leading-relaxed text-slate-700" data-id="element-747" data-mp="2l63lbmqfzkh0">{sentence}</p>
              </div>

              <dl className="mt-4 divide-y divide-slate-100" data-id="element-748" data-mp="2pzpngkyzc5yk">
                {p.highlights.map(h => <div key={h.label} className="flex items-center justify-between py-2.5" data-id="element-749" data-mp="ehb90bp2gi2o">
                    <dt className="text-sm text-muted" data-id="element-750" data-mp="9k2tyjz7su5n">{h.label}</dt>
                    <dd className="text-sm font-semibold text-navy" data-id="element-751" data-mp="naof6fc55g6p">{h.tbd ? <TbdBadge data-id="element-752" data-mp="ja4ej1sczuft" /> : h.value}</dd>
                  </div>)}
              </dl>

              <div className="mt-auto flex flex-wrap items-center justify-between gap-3 border-t border-slate-100 pt-4" data-id="element-753" data-mp="3rpo3holzyqp2">
                <div className="flex items-center gap-3" data-id="element-754" data-mp="3mrmopfvltdje">
                  <div className="flex -space-x-2" data-id="element-755" data-mp="3mt7i3266mqfo">
                    {assigned.slice(0, 5).map(e => <Avatar key={e.id} name={displayName(e)} seed={e.id} size="xs" className="ring-2 ring-white" data-id="element-756" data-mp="3vrj7x1z85k72" />)}
                  </div>
                  <span className="text-xs text-muted" data-id="element-757" data-mp="1fyk6f4ixyfw3">
                    {assigned.length} assigned · updated {formatDate(p.updated, 'MMM d')}
                  </span>
                </div>
                <div className="flex gap-2" data-id="element-758" data-mp="26d1pd7cjyx2q">
                  <Button size="sm" icon={PencilIcon} onClick={() => onNavigate({
                page: 'policy-edit',
                policyId: p.id
              })} data-id="element-759" data-mp="2babm7829ub0l">
                    Edit
                  </Button>
                  <Button size="sm" variant="navy" icon={EyeIcon} onClick={() => onNavigate({
                page: 'policy',
                policyId: p.id
              })} data-id="element-760" data-mp="2gbr8t4k3kpl3">
                    View policy
                  </Button>
                </div>
              </div>
            </article>;
      })}
      </div>
    </div>;
}